// src/components/shell/NavItem.tsx

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface SubItem {
  id: string;
  label: string;
}

export interface NavItemConfig {
  id: string;
  label: string;
  icon: LucideIcon;
  subItems?: SubItem[];
}

interface NavItemProps {
  item: NavItemConfig;
  isActive: boolean;
  activeSubId?: string;
  collapsed: boolean;
  onSelect: (id: string) => void;
  onSelectSub: (itemId: string, subId: string) => void;
}


export function NavItem({
  item,
  isActive,
  activeSubId,
  collapsed,
  onSelect,
  onSelectSub,
}: NavItemProps) {
  const [open, setOpen] = useState(isActive);
  const Icon = item.icon;
  const hasSubs = !!item.subItems?.length;

  const handleClick = () => {
    onSelect(item.id);
    if (hasSubs && !collapsed) setOpen((o) => !o);
  };

  const btn = (
    <button
      onClick={handleClick}
      className={cn(
        'flex items-center gap-2.5 rounded-md transition-colors w-full text-sm',
        collapsed ? 'justify-center p-2' : 'px-2.5 py-2',
        isActive
          ? 'bg-accent text-accent-foreground font-medium'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      <Icon size={16} strokeWidth={isActive ? 2 : 1.5} className="flex-shrink-0" />
      {!collapsed && (
        <>
          <span className="flex-1 text-left">{item.label}</span>
          {hasSubs && (
            <ChevronRight
              size={13}
              className={cn(
                'text-muted-foreground/60 transition-transform duration-200 flex-shrink-0',
                open && 'rotate-90'
              )}
            />
          )}
        </>
      )}
    </button>
  );

  return (
    <div>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{btn}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      ) : (
        btn
      )}

      {/* Sub-items — only when expanded and open */}
      {!collapsed && hasSubs && open && (
        <div className="mt-0.5 ml-6 flex flex-col gap-0.5">
          {item.subItems!.map((sub) => {
            const isSubActive = isActive && activeSubId === sub.id;
            return (
              <button
                key={sub.id}
                onClick={() => {
                  onSelect(item.id);
                  onSelectSub(item.id, sub.id);
                }}
                className={cn(
                  'rounded-md px-2 py-1 text-left text-[12px] capitalize transition-colors',
                  isSubActive
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
              >
                {sub.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}